/**
 * @file ProtectedRoute.jsx
 * @description Route guard component that protects routes requiring authentication
 * @module ProtectedRoute
 */

import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import useAppStore from '../context/AppContext';

/**
 * Protected route wrapper component
 *
 * Checks if user is authenticated before rendering children.
 * Redirects to login if not authenticated.
 * Optionally checks for required role.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @param {string} [props.requiredRole] - Optional role required to access route (e.g., 'ADMIN')
 * @returns {React.ReactElement} Protected content or redirect
 */
function ProtectedRoute({ children, requiredRole }) {
  const location = useLocation();
  const { isAuthenticated, authLoading, user } = useAppStore();

  // Show loading spinner while checking auth state
  if (authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for required role
  if (requiredRole && user?.role !== requiredRole) {
    // User doesn't have required role - redirect to home with error
    return <Navigate to="/" replace />;
  }

  return children;
}

export default ProtectedRoute;
